import React from "react";
import {
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  Layers,
  Zap,
  Clock,
} from "lucide-react";

const PricingRulesDashboard = ({ rules = [] }) => {
  // Calculate statistics
  const stats = {
    total: rules.length,
    active: rules.filter((r) => r.isActive).length,
    inactive: rules.filter((r) => !r.isActive).length,
    highPriority: rules.filter((r) => r.priority >= 50).length,
    expired: rules.filter(
      (r) => r.validUntil && new Date(r.validUntil) < new Date()
    ).length,
    expiringSoon: rules.filter((r) => {
      if (!r.validUntil) return false;
      const daysUntilExpiry = Math.ceil(
        (new Date(r.validUntil) - new Date()) / (1000 * 60 * 60 * 24)
      );
      return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
    }).length,
  };

  // Calculate percentages
  const activePercentage =
    stats.total > 0 ? ((stats.active / stats.total) * 100).toFixed(0) : 0;

  const StatCard = ({
    icon: Icon,
    title,
    value,
    subtitle,
    gradient,
    iconBg,
    alert,
    trend,
  }) => (
    <div
      className={`relative overflow-hidden bg-white rounded-2xl p-5 border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
        alert
          ? "border-danger/30 shadow-danger/10"
          : "border-neutral-light/50 shadow-sm"
      }`}
    >
      {/* Background Gradient */}
      <div
        className={`absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 blur-2xl -translate-y-1/2 translate-x-1/2 ${gradient}`}
      />

      <div className="relative">
        {/* Icon & Trend */}
        <div className="flex items-start justify-between mb-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}
          >
            <Icon className="w-6 h-6 text-white" />
          </div>
          {trend !== undefined && (
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                trend >= 0
                  ? "bg-success/10 text-success-dark"
                  : "bg-danger/10 text-danger"
              }`}
            >
              <TrendingUp
                className={`w-3 h-3 ${trend < 0 ? "rotate-180" : ""}`}
              />
              <span>{Math.abs(trend)}%</span>
            </div>
          )}
          {alert && (
            <div className="flex items-center gap-1 px-2 py-1 bg-danger/10 text-danger rounded-full text-xs font-semibold animate-pulse">
              <AlertCircle className="w-3 h-3" />
              <span>Alert</span>
            </div>
          )}
        </div>

        {/* Value */}
        <div className="mb-1">
          <span className="text-3xl font-bold text-neutral-dark">{value}</span>
          {subtitle && (
            <span className="text-sm text-neutral ml-1">{subtitle}</span>
          )}
        </div>

        {/* Title */}
        <p className="text-sm text-neutral">{title}</p>
      </div>
    </div>
  );

  // Progress Ring Component
  const ProgressRing = ({ percentage }) => {
    const radius = 30;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 transform -rotate-90">
          {/* Background Circle */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            stroke="currentColor"
            strokeWidth="6"
            fill="none"
            className="text-neutral-light"
          />
          {/* Progress Circle */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            stroke="currentColor"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            className="text-success transition-all duration-500"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: strokeDashoffset,
            }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-neutral-dark">
            {percentage}%
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        {/* Total Rules */}
        <StatCard
          icon={Layers}
          title="Total Rules"
          value={stats.total}
          gradient="bg-primary"
          iconBg="bg-gradient-to-br from-primary to-primary-dark"
        />

        {/* Active Rules */}
        <StatCard
          icon={CheckCircle}
          title="Active Rules"
          value={stats.active}
          subtitle={`of ${stats.total}`}
          gradient="bg-success"
          iconBg="bg-gradient-to-br from-success to-success-dark"
          trend={parseInt(activePercentage)}
        />

        {/* Inactive Rules */}
        <StatCard
          icon={XCircle}
          title="Inactive"
          value={stats.inactive}
          gradient="bg-neutral"
          iconBg="bg-gradient-to-br from-neutral to-neutral-dark"
        />

        {/* High Priority */}
        <StatCard
          icon={Zap}
          title="High Priority"
          value={stats.highPriority}
          gradient="bg-warning"
          iconBg="bg-gradient-to-br from-warning to-warning-dark"
        />

        {/* Expiring Soon */}
        <StatCard
          icon={Clock}
          title="Expiring Soon"
          value={stats.expiringSoon}
          gradient="bg-warning"
          iconBg="bg-gradient-to-br from-warning to-danger"
          alert={stats.expiringSoon > 0}
        />

        {/* Expired */}
        <StatCard
          icon={AlertCircle}
          title="Expired"
          value={stats.expired}
          gradient="bg-danger"
          iconBg="bg-gradient-to-br from-danger to-danger-dark"
          alert={stats.expired > 0}
        />
      </div>

      {/* Summary Bar */}
      {stats.total > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-neutral-light/50 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-neutral-dark">
              Rule Status Distribution
            </h4>
            <span className="text-xs text-neutral">
              {activePercentage}% of rules are active
            </span>
          </div>

          {/* Progress Bar */}
          <div className="h-3 bg-neutral-light/50 rounded-full overflow-hidden flex">
            {/* Active */}
            <div
              className="h-full bg-gradient-to-r from-success to-success-dark transition-all duration-500 ease-out"
              style={{ width: `${(stats.active / stats.total) * 100}%` }}
            />
            {/* Inactive */}
            <div
              className="h-full bg-neutral-light transition-all duration-500 ease-out"
              style={{ width: `${(stats.inactive / stats.total) * 100}%` }}
            />
            {/* Expired */}
            <div
              className="h-full bg-gradient-to-r from-danger to-danger-dark transition-all duration-500 ease-out"
              style={{ width: `${(stats.expired / stats.total) * 100}%` }}
            />
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-success" />
              <span className="text-xs text-neutral">
                Active ({stats.active})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-neutral-light" />
              <span className="text-xs text-neutral">
                Inactive ({stats.inactive})
              </span>
            </div>
            {stats.expired > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-danger" />
                <span className="text-xs text-neutral">
                  Expired ({stats.expired})
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingRulesDashboard;
